import React, { useState, useEffect, useRef } from "react";
import Messages from "../components/Messages";
import Input from "../components/Input";
import Loader from "../components/Loader"; // AI Loader component
import axios from "axios";
import API_BASE_URL from "../config";
import { text_faculty } from "../data/faculty";
import { programs_data } from "../data/programs";
import { admission_dates } from "../data/admission_dates";
import { FaGraduationCap, FaChalkboardTeacher, FaCalendarAlt, FaRobot, FaTimes } from "react-icons/fa";
import { motion } from "framer-motion";

const fileNames = [
  { name: "Dates", icon: <FaCalendarAlt />, file: admission_dates },
  { name: "Faculty", icon: <FaChalkboardTeacher />, file: text_faculty },
  { name: "Programs", icon: <FaGraduationCap />, file: programs_data },
];


export default function Chatbot({ onClose }) {
  const [messages, setMessages] = useState([]);
  const [categorySelected, setCategorySelected] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [fileContent, setFileContent] = useState("");
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setMessages([
      {
        id: "msg-0",
        role: "bot",
        content: "Welcome to Sukkur IBA! I'm your AI assistant. Please select a topic below so I can help you better."
      }
    ]);
  }, [onClose]);

  const handleCategoryClick = async (category) => {
    if (selectedCategory === category) return; // Already on this topic

    setSelectedCategory(category);
    setCategorySelected(true);
    const selectedFile = fileNames.find((f) => f.name === category)?.file;
    setFileContent(selectedFile);

    setMessages(prev => [
      ...prev,
      {
        id: `sys-${Date.now()}`,
        role: "bot",
        content: `I've switched focus to **${category}**. You can now ask questions about this area, and I'll still remember our previous chat!`
      }
    ]);
  };

  const [isTyping, setIsTyping] = useState(false);

  const send = async (text) => {
    if (!fileContent) return;

    const userMsgId = `user-${Date.now()}`;
    const botMsgId = `bot-${Date.now()}`;

    // Add user message immediately
    setMessages(prev => [
      ...prev,
      { id: userMsgId, role: "user", content: text }
    ]);

    setIsTyping(true);

    const prompt = `
      You are the IBA Smart Assistant. 
      
      ### REFERENCE DATA:
      ${fileContent}
      
      ### USER REQUEST:
      ${text}
      
      ### FORMATTING INSTRUCTIONS:
      Return a clear, concise response.
      - Use **vertical lists** with dashes (-) for multiple items.
      - **Bold** key terms and dates with double asterisks.
      - Use **Double Newlines** between sections.
      - No fluff. Direct answers only.
      
      Answer ONLY based on the provided data.
    `;
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/chat`, { prompt });
      const answer = response.data?.content || "I'm sorry, I seem to be having trouble connecting to my brain right now.";
      
      setIsTyping(false);
      setMessages(prev => [
        ...prev,
        { id: botMsgId, role: "bot", content: answer }
      ]);
    } catch (error) {
       console.error("Error with Backend Chat API:", error);
       setIsTyping(false);
       setMessages(prev => [
         ...prev,
         { id: botMsgId, role: "bot", content: "Sorry, I couldn't process your request at the moment." }
       ]);
    }
  };


  return (
    <motion.div 
      initial={{ opacity: 0, y: 50, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 50, scale: 0.95 }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className="chatbot-main-window"
    >
        {/* Header - Ultra Compact */}
        <div className="chatbot-glass-header">
          <div className="header-left-side">
            <div className="bot-pulse-avatar">
              <FaRobot />
              <span className="live-dot-pulse"></span>
            </div>
            <div className="header-info">
              <h4>IBA Smart Assistant</h4>
              <div className="status-row">
                <span className="dot"></span>
                <span>Active Now</span>
              </div>
            </div>
          </div>
          
          <button className="header-close-btn" onClick={onClose} aria-label="Close Chat">
            <FaTimes />
          </button>
        </div>

        {/* Messages Area */}
        <div className="chatbot-chat-scroll custom-scrollbar">
          <div className="chat-internal-padding">
             <Messages messages={messages} />
             {isTyping && (
               <div className="typing-indicator-wrap">
                 <div className="typing-bubble">
                   <Loader />
                 </div>
               </div>
             )}
             <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Controls Area */}
        <div className="chatbot-footer-stage">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="topic-selection-tray"
          >
            <div className="topic-horizontal-row">
              {fileNames.map((item, index) => (
                <motion.button
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(0, 206, 209, 0.1)" }}
                  whileTap={{ scale: 0.95 }}
                  key={index}
                  onClick={() => handleCategoryClick(item.name)}
                  className={`topic-pill ${selectedCategory === item.name ? "active" : ""}`}
                >
                  <span className="pill-icon">{item.icon}</span>
                  <span className="pill-name">{item.name}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>

          <div className="input-field-wrapper">
             <Input 
               onSend={send} 
               disabled={!categorySelected} 
               placeholder={categorySelected ? "Ask me anything..." : "Quick select a topic above"} 
             />
          </div>
        </div>

        <style>{`
          .chatbot-main-window {
            position: fixed;
            bottom: 105px;
            right: 30px;
            width: 380px;
            height: 550px;
            max-width: calc(100vw - 40px);
            max-height: calc(100vh - 140px);
            background: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(25px) saturate(200%);
            -webkit-backdrop-filter: blur(25px) saturate(200%);
            border-radius: 20px;
            box-shadow: 0 25px 60px -15px rgba(0, 30, 60, 0.45);
            display: flex;
            flex-direction: column;
            overflow: hidden;
            z-index: 29999;
            border: 1px solid rgba(255, 255, 255, 0.4);
            font-family: 'Inter', sans-serif;
          }

          /* Header Styling - Ultra Compact */
          .chatbot-glass-header {
            background: linear-gradient(135deg, #001e3c 0%, #004080 100%);
            padding: 14px 20px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            color: white;
            flex-shrink: 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }

          .header-left-side {
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .bot-pulse-avatar {
            width: 32px;
            height: 32px;
            background: rgba(0, 206, 209, 0.15);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1rem;
            color: #00ced1;
            position: relative;
            border: 1px solid rgba(0, 206, 209, 0.3);
          }

          .live-dot-pulse {
            position: absolute;
            bottom: 0px;
            right: 0px;
            width: 8px;
            height: 8px;
            background: #10b981;
            border: 1.5px solid #001e3c;
            border-radius: 50%;
            box-shadow: 0 0 8px rgba(16, 185, 129, 0.6);
          }

          .header-close-btn {
            background: rgba(255, 255, 255, 0.1);
            border: none;
            color: white;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: 0.2s;
            font-size: 0.9rem;
          }

          .header-close-btn:hover {
            background: rgba(255, 255, 255, 0.2);
            color: #00ced1;
          }

          .header-info h4 {
            font-size: 0.85rem;
            font-weight: 700;
            margin: 0;
            letter-spacing: -0.2px;
          }

          .status-row {
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 0.6rem;
            font-weight: 600;
            color: #00ced1;
            text-transform: uppercase;
            letter-spacing: 0.4px;
          }

          .status-row .dot {
            width: 5px;
            height: 5px;
            background: #10b981;
            border-radius: 50%;
          }

          .action-icon-btn {
            background: rgba(255, 255, 255, 0.1);
            border: none;
            color: white;
            width: 32px;
            height: 32px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            margin-left: 8px;
            transition: 0.2s;
          }

          .action-icon-btn:hover {
            background: rgba(255, 255, 255, 0.2);
            color: #00ced1;
          }

          /* Chat Area */
          .chatbot-chat-scroll {
            flex: 1;
            overflow-y: auto;
            background: rgba(248, 250, 252, 0.3);
          }

          .chat-internal-padding {
            padding: 20px;
          }

          .typing-indicator-wrap {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-top: 10px;
          }

          .bot-pulse-avatar.small {
            width: 28px;
            height: 28px;
            font-size: 0.9rem;
            border-radius: 8px;
          }

          .typing-bubble {
            background: white;
            padding: 8px 14px;
            border-radius: 4px 14px 14px 14px;
            border: 1px solid rgba(0,0,0,0.05);
          }

          /* Footer & Topic Pills */
          .chatbot-footer-stage {
            padding: 12px 16px 16px;
            background: white;
            border-top: 1px solid rgba(0, 0, 0, 0.05);
            flex-shrink: 0;
          }

          .topic-selection-tray {
            margin-bottom: 10px;
          }

          .topic-horizontal-row {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 6px;
            width: 100%;
          }

          .topic-pill {
            background: #f8fafc;
            border: 1px solid rgba(0, 0, 0, 0.05);
            border-radius: 40px;
            padding: 5px 10px;
            display: flex;
            align-items: center;
            gap: 5px;
            cursor: pointer;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            user-select: none;
            flex: 1;
            justify-content: center;
            max-width: 100px;
          }

          .pill-icon {
            font-size: 0.8rem;
            color: #004080;
          }

          .pill-name {
            font-size: 0.68rem;
            font-weight: 700;
            color: #475569;
            letter-spacing: -0.1px;
          }

          .topic-pill:hover {
            background: white;
            border-color: #00ced1;
            box-shadow: 0 4px 10px rgba(0, 206, 209, 0.15);
            transform: translateY(-1px);
          }

          .topic-pill.active {
            background: linear-gradient(135deg, #004080 0%, #001e3c 100%);
            color: white;
            border: none;
            box-shadow: 0 4px 12px rgba(0, 64, 128, 0.25);
          }

          .topic-pill.active .pill-name, .topic-pill.active .pill-icon {
            color: white;
          }

          /* Custom Scrollbar */
          .custom-scrollbar::-webkit-scrollbar { width: 4px; }
          .custom-scrollbar::-webkit-scrollbar-thumb { 
            background: rgba(0, 43, 90, 0.1); 
            border-radius: 10px; 
          }

          @media (max-width: 576px) {
            .chatbot-main-window {
              bottom: 0px;
              right: 0px;
              width: 100vw;
              height: 100vh;
              max-width: 100vw;
              max-height: 100vh;
              border-radius: 0;
            }
            .topic-pill {
              max-width: none;
            }
          }
        `}</style>
    </motion.div>
  );
}
